/**
 * Losi App Definition
 *
 * Validates the controlled structure used to render and run Apps.
 *
 * Primary responsibilities:
 * - Define allowed page, block, field, and action types
 * - Reject invalid generated app definitions
 *
 * Dependencies:
 * - zod
 */

import { z } from 'zod'

import { appActionExecutionStrategySchema } from '@/lib/apps/capabilities'
import type { AppActionExecutionStrategy } from '@/lib/apps/capabilities'

export const APP_DEFINITION_VERSION = 1

export const appPageTypes = [
  'dashboard',
  'table',
  'form',
  'detail',
  'kanban',
  'checklist',
  'report',
  'gallery',
  'logs',
  'settings'
] as const

export const appBlockTypes = [
  'metric_card',
  'text_block',
  'action_panel',
  'record_table',
  'record_kanban',
  'record_form',
  'report_view',
  'checklist',
  'output_list',
  'run_timeline',
  'setup_checklist',
  'empty_state',
  'chart_basic'
] as const

export const appFieldTypes = [
  'text',
  'long_text',
  'number',
  'boolean',
  'date',
  'url',
  'email',
  'select',
  'multi_select',
  'status',
  'priority',
  'relation',
  'file',
  'json'
] as const

export const appActionTypes = [
  'ai.generateText',
  'ai.summarize',
  'ai.classify',
  'ai.prioritize',
  'ai.review',
  'ai.generateChecklist',
  'ai.generateReport',
  'ai.extractFromText',
  'ai.generateCopy',
  'ai.generateImagePrompt',
  'image.generate',
  'image.edit',
  'spaces.createTaskDraft',
  'spaces.createTask',
  'spaces.createNoteDraft',
  'spaces.createNote',
  'web.fetch',
  'file.create',
  'approval.request'
] as const

export const appReadinessStatuses = [
  'ready',
  'needs_input',
  'needs_connection',
  'limited_ready'
] as const

const keySchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z][a-z0-9_]*$/)

const labelSchema = z.string().min(1).max(120)
const textSchema = z.string().max(2000)
const jsonRecordSchema = z.record(z.unknown())

export const appFieldOptionSchema = z.object({
  label: labelSchema,
  value: z.string().min(1).max(120)
})

export const appFieldSchema = z.object({
  key: keySchema,
  label: labelSchema,
  type: z.enum(appFieldTypes),
  required: z.boolean().default(false),
  options: z.array(appFieldOptionSchema).default([]),
  relationModel: keySchema.optional(),
  placeholder: z.string().max(160).optional(),
  helpText: z.string().max(300).optional()
})

export const appModelSchema = z.object({
  key: keySchema,
  label: labelSchema,
  pluralLabel: labelSchema.optional(),
  description: textSchema.optional(),
  fields: z.array(appFieldSchema).min(1).max(32),
  primaryField: keySchema.optional()
})

export const appBlockSchema = z.object({
  key: keySchema,
  title: labelSchema.optional(),
  type: z.enum(appBlockTypes),
  modelKey: keySchema.optional(),
  actionKeys: z.array(keySchema).default([]),
  outputTypes: z.array(z.string().min(1).max(80)).default([]),
  config: jsonRecordSchema.default({})
})

export const appPageSchema = z.object({
  key: keySchema,
  label: labelSchema,
  type: z.enum(appPageTypes),
  modelKey: keySchema.optional(),
  blocks: z.array(appBlockSchema).default([])
})

export const appActionSchema = z.object({
  key: keySchema,
  label: labelSchema,
  description: textSchema.optional(),
  type: z.enum(appActionTypes),
  inputModel: keySchema.optional(),
  outputModel: keySchema.optional(),
  approvalRequired: z.boolean().default(false),
  primary: z.boolean().default(false),
  connectionRequirements: z.array(z.string().min(1).max(80)).default([]),
  executionStrategy: appActionExecutionStrategySchema.optional(),
  prompt: textSchema.optional()
})

export const appWorkflowSchema = z.object({
  key: keySchema,
  label: labelSchema,
  description: textSchema.optional(),
  actionKeys: z.array(keySchema).default([])
})

export const appReadinessInputSchema = z.object({
  key: keySchema,
  label: labelSchema,
  type: z.enum(appFieldTypes),
  reason: z.string().min(1).max(300),
  modelKey: keySchema.optional(),
  fieldKey: keySchema.optional()
})

export const appReadinessFirstActionSchema = z.object({
  key: keySchema,
  label: labelSchema
})

export const appReadinessSchema = z.object({
  status: z.enum(appReadinessStatuses).default('limited_ready'),
  requiredInputs: z.array(appReadinessInputSchema).default([]),
  optionalImprovements: z.array(appReadinessInputSchema).default([]),
  firstBestAction: appReadinessFirstActionSchema.nullable().default(null),
  demoAvailable: z.boolean().default(true)
})

export const appSampleRecordSchema = z.object({
  modelKey: keySchema,
  title: labelSchema,
  data: jsonRecordSchema,
  status: z.string().max(80).optional()
})

export const appDefinitionSchema = z.object({
  version: z.literal(APP_DEFINITION_VERSION),
  app: z.object({
    name: z.string().min(1).max(140),
    description: z.string().min(1).max(1000),
    icon: z.string().min(1).max(64).default('losi')
  }),
  navigation: z.array(appPageSchema).min(1).max(16),
  models: z.array(appModelSchema).min(1).max(24),
  actions: z.array(appActionSchema).min(1).max(32),
  workflows: z.array(appWorkflowSchema).default([]),
  readiness: appReadinessSchema,
  settings: jsonRecordSchema.default({}),
  permissions: jsonRecordSchema.default({}),
  sampleRecords: z.array(appSampleRecordSchema).default([]),
  suggestedPrompts: z.array(z.string().min(1).max(160)).default([])
})

export type AppPageType = (typeof appPageTypes)[number]
export type AppBlockType = (typeof appBlockTypes)[number]
export type AppFieldType = (typeof appFieldTypes)[number]
export type AppActionType = (typeof appActionTypes)[number]
export type AppReadinessStatus = (typeof appReadinessStatuses)[number]

export type AppField = z.infer<typeof appFieldSchema>
export type AppModel = z.infer<typeof appModelSchema>
export type AppBlock = z.infer<typeof appBlockSchema>
export type AppPage = z.infer<typeof appPageSchema>
export type AppAction = z.infer<typeof appActionSchema>
export type { AppActionExecutionStrategy }
export type AppWorkflow = z.infer<typeof appWorkflowSchema>
export type AppReadiness = z.infer<typeof appReadinessSchema>
export type AppSampleRecord = z.infer<typeof appSampleRecordSchema>
export type AppDefinition = z.infer<typeof appDefinitionSchema>

export interface AppValidationResult {
  ok: boolean
  definition?: AppDefinition
  issues: string[]
}

// --------------

export function parseAppDefinition(value: unknown): AppDefinition {
  const definition = appDefinitionSchema.parse(value)
  assertDefinitionReferences(definition)
  return definition
}

// --------------

export function validateAppDefinition(value: unknown): AppValidationResult {
  const parsed = appDefinitionSchema.safeParse(value)

  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => issue.message)
    }
  }

  try {
    assertDefinitionReferences(parsed.data)
  } catch (error) {
    return {
      ok: false,
      issues: [error instanceof Error ? error.message : 'Invalid app definition']
    }
  }

  return {
    ok: true,
    definition: parsed.data,
    issues: []
  }
}

// --------------

export function getPrimaryAction(definition: AppDefinition): AppAction | null {
  return (
    definition.actions.find((action) => action.primary) ??
    definition.actions.find((action) => action.key === definition.readiness.firstBestAction?.key) ??
    definition.actions[0] ??
    null
  )
}

// --------------

export function getModelByKey(definition: AppDefinition, modelKey: string): AppModel | null {
  return definition.models.find((model) => model.key === modelKey) ?? null
}

// --------------

function assertDefinitionReferences(definition: AppDefinition) {
  const modelKeys = new Set(definition.models.map((model) => model.key))
  const actionKeys = new Set(definition.actions.map((action) => action.key))
  const pageKeys = new Set<string>()

  for (const page of definition.navigation) {
    if (pageKeys.has(page.key)) {
      throw new Error(`Duplicate tab key: ${page.key}`)
    }

    pageKeys.add(page.key)

    if (page.modelKey && !modelKeys.has(page.modelKey)) {
      throw new Error(`Tab "${page.label}" references an unknown record type`)
    }

    for (const block of page.blocks) {
      if (block.modelKey && !modelKeys.has(block.modelKey)) {
        throw new Error(`Block "${block.key}" references an unknown record type`)
      }

      for (const actionKey of block.actionKeys) {
        if (!actionKeys.has(actionKey)) {
          throw new Error(`Block "${block.key}" references an unknown action`)
        }
      }
    }
  }

  for (const action of definition.actions) {
    if (action.inputModel && !modelKeys.has(action.inputModel)) {
      throw new Error(`Action "${action.label}" has an unknown input type`)
    }

    if (action.outputModel && !modelKeys.has(action.outputModel)) {
      throw new Error(`Action "${action.label}" has an unknown output type`)
    }
  }

  for (const workflow of definition.workflows) {
    for (const actionKey of workflow.actionKeys) {
      if (!actionKeys.has(actionKey)) {
        throw new Error(`Workflow "${workflow.label}" references an unknown action`)
      }
    }
  }
}
